import React, { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { AccessDenied, canAccess } from '../adminAccess';
import DatabaseError from '../components/DatabaseError';
import Table from '../components/Table';
import { AccountBadge, DateTime, PageHeader } from '../components/AdminUI';

export default function ClientsPage({ user }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true); setError('');
      const result = await httpsCallable(functions, 'listManagedUsers')({ role: 'client' });
      setClients(result.data?.users || []);
    } catch (err) { setError(err?.message || 'Unable to load clients.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function toggle(account) {
    try {
      await httpsCallable(functions, 'manageUserAccess')({
        uid: account.uid,
        action: account.status === 'disabled' || account.status === 'blocked' ? 'reenable' : 'disable',
      });
      await load();
    } catch (err) { setError(err?.message || 'Unable to update client.'); }
  }

  // "Hesabı sil" (Delete Account), email channel (ToS section 11, path B):
  // use ONLY after verifying the requester's name, phone, and email match
  // this account. This starts the exact same 7-business-day / 3-month
  // clock as the customer's own self-service in-app request -- the actual
  // anonymization/Auth deletion only ever happens in the scheduled
  // processAccountDeletions sweep, never here.
  async function requestDeletion(account) {
    const confirmed = window.confirm(
      'Start account deletion for this client? Use this ONLY after ' +
      'verifying their name, phone, and email match the account on file. ' +
      'This begins the same 7-business-day / 3-month deletion process as ' +
      'the customer\'s own self-service request and cannot be undone ' +
      'through this panel.',
    );
    if (!confirmed) return;
    try {
      await httpsCallable(functions, 'manageUserAccess')({
        uid: account.uid,
        action: 'request_deletion',
      });
      await load();
    } catch (err) { setError(err?.message || 'Unable to start account deletion.'); }
  }

  async function edit(account) {
    const name = window.prompt('Full name', account.name || '');
    if (!name) return;
    const phone = window.prompt('Phone', account.phone || '');
    if (phone === null) return;
    try {
      await httpsCallable(functions, 'updateManagedUser')({ uid: account.uid, name, phone });
      await load();
    } catch (err) { setError(err?.message || 'Unable to update client.'); }
  }

  if (!canAccess(user, 'users')) return <AccessDenied />;
  return <div className="page-shell">
    <PageHeader title="Clients" description="Manage approved PakMazar clients and their account access.">
      <button className="btn-primary" onClick={() => setShowAdd(true)}>+ Add Client</button>
      <button className="btn-secondary" onClick={load} disabled={loading}>Refresh</button>
    </PageHeader>
    <DatabaseError message={error} onRetry={load} />
    {loading ? <p className="py-10 text-center text-gray-500">Loading...</p> : <Table
      headers={['Client', 'Email', 'Phone', 'Status', 'Verification', 'Created', 'Last updated', 'Actions']}
      empty="No clients found"
    >
      {clients.map(account => {
        const isDeletionRequested = account.status === 'deletion_requested';
        // A brief scheduled-sweep processing lease (external review round 2,
        // finding #8) -- shown the same as "Deletion requested" since it's
        // an implementation detail, not a distinct state an admin needs to
        // act on differently.
        const isDeletionProcessing = account.status === 'deletion_processing';
        const isDeleted = account.status === 'deleted';
        const isBeingDeleted =
          isDeletionRequested || isDeletionProcessing || isDeleted;
        return <tr key={account.uid} className="border-b border-gray-50 text-sm">
        <td className="px-4 py-3 font-medium">{account.name || '—'}</td>
        <td className="px-4 py-3">{account.email || '—'}</td>
        <td className="px-4 py-3">{account.phone || '—'}</td>
        <td className="px-4 py-3">{isBeingDeleted
          ? <AccountBadge kind="deletion">{isDeleted ? 'Deleted' : 'Deletion requested'}</AccountBadge>
          : <AccountBadge kind={account.isActive ? 'active' : 'disabled'}>{account.isActive ? 'Active' : 'Disabled'}</AccountBadge>}</td>
        <td className="px-4 py-3"><AccountBadge kind={account.emailVerified ? 'verified' : 'unverified'}>{account.emailVerified ? 'Verified' : 'Unverified'}</AccountBadge></td>
        <td className="px-4 py-3"><DateTime value={account.registrationDate} /></td>
        <td className="px-4 py-3"><DateTime value={account.updatedAt} /></td>
        <td className="px-4 py-3"><div className="flex flex-wrap gap-2">
          <button className="btn-secondary" onClick={() => window.alert(JSON.stringify(account, null, 2))}>View</button>
          {!isBeingDeleted && <>
            <button className="btn-secondary" onClick={() => edit(account)}>Edit</button>
            <button className="btn-secondary" onClick={() => toggle(account)}>{account.isActive ? 'Disable' : 'Enable'}</button>
            <button className="btn-danger" onClick={() => requestDeletion(account)}>Request Deletion</button>
          </>}
        </div></td>
      </tr>;
      })}
    </Table>}
    {showAdd && <AddClient onClose={() => setShowAdd(false)} onAdded={async () => { setShowAdd(false); await load(); }} setError={setError} />}
  </div>;
}

function AddClient({ onClose, onAdded, setError }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [saving, setSaving] = useState(false);
  async function submit(event) {
    event.preventDefault(); setSaving(true); setError('');
    try {
      await httpsCallable(functions, 'createManagedUser')({ ...form, role: 'client' });
      await onAdded();
    } catch (err) { setError(err?.message || 'Unable to create client.'); }
    finally { setSaving(false); }
  }
  return <div className="fixed inset-0 bg-[#251a28]/55 z-50 flex items-center justify-center p-4 backdrop-blur-sm" role="presentation" onMouseDown={e => e.target === e.currentTarget && onClose()}>
    <form onSubmit={submit} className="bg-white p-6 w-full max-w-md space-y-4 shadow-2xl" style={{ borderRadius: 24 }} role="dialog" aria-modal="true" aria-label="Add Client">
      <h2 className="font-bold text-lg">Add Client</h2>
      {['name', 'email', 'phone', 'password'].map(key => <label key={key} className="block text-sm font-semibold text-gray-700">{key === 'name' ? 'Full name' : key[0].toUpperCase() + key.slice(1)}<input className="input-field mt-1.5 w-full"
        type={key === 'password' ? 'password' : key === 'email' ? 'email' : 'text'}
        required={key !== 'phone'} value={form[key]}
        onChange={event => setForm(current => ({ ...current, [key]: event.target.value }))} /></label>)}
      <div className="flex gap-2"><button type="button" className="btn-secondary flex-1" onClick={onClose}>Cancel</button>
        <button className="btn-primary flex-1" disabled={saving}>{saving ? 'Saving...' : 'Add Client'}</button></div>
    </form>
  </div>;
}
